import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import { colour, pillSx } from '../admin/tokens';
import { formatMoney } from '../../utils/money';
import { durationLabel, momentLabel } from '../../utils/approvalSummary';

/**
 * Where a family's money and their TASS status sit, at the top of the review.
 *
 * A reviewer deciding whether to accept a change asks the same three questions every time: what
 * did they pay, how much of their own credit went towards it, and has any of this reached TASS
 * yet. All three lived on other screens, or nowhere.
 */

const Figure = ({ label, value, strong = false, hint }) => (
    <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
        <Typography sx={{ fontSize: '11px', color: colour.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
        </Typography>
        <Typography sx={{
            fontSize: strong ? '18px' : '15px',
            fontWeight: strong ? 800 : 700,
            color: strong ? colour.ink : colour.body,
            mt: 0.25,
        }}>
            {value}
        </Typography>
        {hint && <Typography sx={{ fontSize: '11px', color: colour.faint }}>{hint}</Typography>}
    </Box>
);

/**
 * The two TASS steps, shown as two things because they are two things: the receipt goes as soon
 * as the family pays, the details only once somebody has approved them.
 */
export const SyncPills = ({ sync = {}, size = 'small', showDetails = true }) => {
    const receipted = sync.receipting === 'synced';
    const details = sync.details || 'pending';
    const stage = String(sync.reenrolment || '').trim().toLowerCase();
    const finished = stage === 'completed' || stage === 'synced';

    // Neither TASS step has happened or can happen yet, and saying "receipt pending" about a
    // family who has not paid reads as something outstanding on our side rather than theirs.
    if (!finished) {
        return (
            <Chip
                size={size}
                label={stage === 'rejected' ? 'Payment rejected' : 'Still in progress'}
                sx={pillSx(stage === 'rejected' ? 'bad' : 'neutral')}
            />
        );
    }

    const detailTone = details === 'synced' ? 'good' : (details === 'approved' ? 'info' : 'warn');
    // Named for the detail sync specifically. "Waiting for approval" would read identically to
    // the queue's own name and to the count above it, and the three mean different things.
    const detailWord = details === 'synced'
        ? 'Details in TASS'
        : (details === 'approved' ? 'Approved, not yet sent' : 'Details not approved');

    return (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size={size} label={receipted ? 'Receipted' : 'Receipt pending'}
                sx={pillSx(receipted ? 'good' : 'warn')} />
            {showDetails && <Chip size={size} label={detailWord} sx={pillSx(detailTone)} />}
        </Box>
    );
};

/**
 * What a family owed us from before this year, and paid off in the same transaction as their
 * re-enrolment — or null when they owed nothing, which is the ordinary case and needs no line.
 *
 * It is the one figure in the payment that the per-child fees underneath cannot explain, so it is
 * also the only one whose absence makes the rest look wrong.
 */
const clearedDebt = (payment) => {
    const owed = Number(payment?.outstanding);
    return Number.isFinite(owed) && owed > 0 ? owed : null;
};

/** What of their payment went to this year, once the older balance is set aside. */
const thisYearPaid = (payment) => Math.max(0, Number(payment?.paidAmount || 0) - (clearedDebt(payment) || 0));

/**
 * The receipt number, and the amount asked for when it is not the amount that arrived.
 *
 * On every paid row to date the two match, so naming the asked-for amount as its own figure read
 * as "paid in full, nothing outstanding" directly above a line saying thousands were still owed.
 * It is worth saying only when it differs, which is the case where it matters.
 */
const paidHint = (payment) => {
    const asked = Number(payment?.total);
    const arrived = Number(payment?.paidAmount);
    const short = Number.isFinite(asked) && Number.isFinite(arrived) && Math.abs(asked - arrived) >= 0.005
        ? `of ${formatMoney(asked)} asked`
        : null;
    const receipt = payment?.receiptNumber ? `Receipt ${payment.receiptNumber}` : null;
    return [short, receipt].filter(Boolean).join(' · ') || undefined;
};

/**
 * `payment` missing entirely is the enrolment team: the server did not send them the figures,
 * and this draws nothing rather than "no payment recorded yet", which would be a statement
 * about the family instead of about who is reading.
 */
/**
 * When they started, when they finished, and how long that took.
 *
 * Shown to both teams: it is neither money nor medical, and it is the question being asked of
 * the reporting — how long does a re-enrolment actually take a family.
 */
const Timing = ({ timing }) => {
    if (!timing?.startedAt) return null;

    const took = durationLabel(timing.minutesToComplete);

    return (
        <Typography sx={{ fontSize: '12px', color: colour.muted, mt: 1.25 }}>
            {`Started ${momentLabel(timing.startedAt)}`}
            {timing.completedAt
                ? ` · finished ${momentLabel(timing.completedAt)}${took ? ` · took ${took}` : ''}`
                : ' · not finished yet'}
        </Typography>
    );
};

const FamilySummary = ({ family = {}, payment, sync = {}, timing, showDetailSync = true }) => (
    <Box sx={{
        bgcolor: colour.stage,
        border: `1px solid ${colour.border}`,
        borderRadius: '12px',
        p: { xs: 1.5, sm: 2 },
        mb: 2.5,
    }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <SyncPills sync={sync} showDetails={showDetailSync} />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {family.healthCareCard && (
                    // The expiry is the whole question for AR: the discount is applied against a
                    // card that is still valid when the year is billed, so the date belongs
                    // beside the claim rather than a click away.
                    <Chip
                        size="small"
                        label={family.healthCareCardExpiry
                            ? `Health care card · expires ${family.healthCareCardExpiry}`
                            : 'Health care card'}
                        sx={pillSx('info')}
                    />
                )}
                {family.staffMember && <Chip size="small" label="Staff discount" sx={pillSx('info')} />}
                {family.children > 0 && (
                    <Chip size="small" label={`${family.children} ${family.children === 1 ? 'child' : 'children'}`} sx={pillSx('neutral')} />
                )}
            </Box>
        </Box>

        {!payment ? null : payment.paid ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, rowGap: 1.5 }}>
                <Figure
                    label="Paid"
                    value={formatMoney(payment.paidAmount)}
                    strong
                    hint={paidHint(payment)}
                />
                {clearedDebt(payment) !== null && [
                    // The two halves of what they paid, because the per-child fees below add up to
                    // the first and say nothing about the second. Without these a reviewer sees
                    // $900 of fees against a $2,330.84 payment and cannot tell where the rest went.
                    <Figure key="year" label="This year's fees" value={formatMoney(thisYearPaid(payment))} />,
                    <Figure
                        key="debt"
                        label="Older balance cleared"
                        value={formatMoney(clearedDebt(payment))}
                        hint="owed before this year"
                    />,
                ]}
                <Figure label="By card" value={formatMoney(payment.fromCard)} />
                <Figure
                    label="Credit used"
                    value={formatMoney(payment.fromCredit)}
                    hint={payment.creditAvailable ? `${formatMoney(payment.creditAvailable)} available` : undefined}
                />
                <Figure
                    label="School fees left to pay"
                    value={formatMoney(payment.remaining)}
                    hint="over the rest of the year"
                />
            </Box>
        ) : (
            <Typography sx={{ fontSize: '13px', color: colour.muted }}>
                No payment recorded yet
                {payment.creditAvailable
                    ? ` — ${formatMoney(payment.creditAvailable)} of credit available to them.`
                    : '.'}
            </Typography>
        )}

        <Timing timing={timing} />
    </Box>
);

export default FamilySummary;
